import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import React, { useEffect, useState } from "react";
import journalAdminServices from "../../../services/journal/admin";
import { journalListTableColumn } from "../../../helpers/constant";
import { Navigate, useNavigate } from "react-router-dom";
import { CustomDialog, CustomIconMenu, Loader } from "../../../components";
import moment from "moment";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.surface,
    color: theme.palette.neutral[90],
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.info.main,
  cursor: "pointer",
}));

const WarningDescText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[90],
}));

const JournalList = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [deleteJournal, setDeleteJournal] = useState({
    id: null,
    warningOpen: false,
    loading: false,
  });

  const [journalList, setJournalList] = useState({
    data: [],
    loading: false,
    refresh: false,
    filter: {
      sortBy: "",
      sortDirection: "",
      page: 0,
      rowsPerPage: 10,
      totalDocs: 0,
    },
  });

  const handleChangePage = (event, newPage) => {
    setJournalList((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        page: newPage,
      },
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setJournalList((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        rowsPerPage: event.target.value,
        page: 0,
      },
    }));
  };

  useEffect(() => {
    const fetchAllJournals = async () => {
      setJournalList((prev) => ({
        ...prev,
        loading: true,
      }));

      const reqAllJournals = {
        searchKey: "",
        sortBy: journalList.filter.sortBy,
        sortDirection: journalList.filter.sortDirection,
        page: journalList.filter.page + 1,
        limit: journalList.filter.rowsPerPage,
      };

      const resAllJournals = await journalAdminServices.getJournalList(
        reqAllJournals
      );

      if (resAllJournals?.data?.docs?.length > 0) {
        setJournalList((prev) => ({
          ...prev,
          data: resAllJournals.data.docs,
          loading: false,
          filter: {
            ...prev.filter,
            totalDocs: resAllJournals.data.totalDocs,
          },
        }));
      } else {
        setJournalList((prev) => ({
          ...prev,
          data: [],
          loading: false,
        }));
      }
    };
    fetchAllJournals();
  }, [
    journalList.filter.page,
    journalList.filter.rowsPerPage,
    journalList.refresh,
    journalList.filter.sortBy,
    journalList.filter.sortDirection,
  ]);

  const rowActionOptions = (rowData) => [
    {
      name: "Delete",
      handleOption: () => {
        setDeleteJournal((prev) => ({
          ...prev,
          id: rowData._id,
          warningOpen: true,
        }));
      },
    },
  ];

  const onClickDeleteSubject = async () => {
    if (!deleteJournal?.id) {
      return;
    }
    setDeleteJournal((prev) => ({
      ...prev,
      loading: true,
    }));

    const deleteRes = await journalAdminServices.deleteJournal({
      journalId: deleteJournal?.id,
    });

    if (deleteRes?.data) {
      setJournalList((prev) => ({
        ...prev,
        refresh: !prev.refresh,
      }));
      setDeleteJournal((prev) => ({
        ...prev,
        id: null,
        warningOpen: false,
        loading: false,
      }));
    } else {
      setDeleteJournal((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginBottom: "10px",
        }}
      >
        <Button
          sx={{
            "&.MuiButton-root": {
              color: theme.palette.neutral[0],
              backgroundColor: theme.palette.secondary.main,
              textTransform: "none",

              "&:hover": {
                backgroundColor: theme.palette.secondary.hover,
              },
            },
          }}
          size="small"
          onClick={() => navigate(`/admin/journal/add`)}
        >
          Create Journal
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              {journalListTableColumn?.map(
                (journalListColumn, journalListColumnIndex) => (
                  <StyledTableCell
                    size="small"
                    key={`${journalListColumnIndex}_${journalListColumn}`}
                  >
                    {journalListColumn}
                  </StyledTableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {journalList?.loading ? (
              <StyledTableRow
                key={`subject_not_found`}
                sx={{ height: 30 * journalList?.filter?.rowsPerPage }}
              >
                <StyledTableCell
                  scope="row"
                  colSpan={6}
                  sx={{ textAlign: "center" }}
                >
                  <Loader />
                </StyledTableCell>
              </StyledTableRow>
            ) : journalList?.data?.length > 0 ? (
              journalList?.data?.map((journalRow) => (
                <StyledTableRow key={`${journalRow._id}${journalRow.name}`}>
                  <StyledTableCell size="small" component="th" scope="row">
                    <StyledTitle
                      onClick={() =>
                        navigate(
                          `/admin/journal/edit?journalId=${journalRow._id}`
                        )
                      }
                    >
                      {journalRow.name}
                    </StyledTitle>
                  </StyledTableCell>
                  <StyledTableCell size="small">
                    {journalRow?.journalLanguage}
                  </StyledTableCell>
                  <StyledTableCell size="small">
                    {journalRow?.origin}
                  </StyledTableCell>
                  <StyledTableCell size="small">
                    {journalRow?.updatedBy}
                  </StyledTableCell>
                  <StyledTableCell size="small">
                    {moment(journalRow?.updatedAt).format("DD-MM-YYYY")}
                  </StyledTableCell>
                  <StyledTableCell size="small">
                    <CustomIconMenu options={rowActionOptions(journalRow)} />
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow key={`subject_not_found`}>
                <StyledTableCell
                  size="small"
                  scope="row"
                  colSpan={4}
                  sx={{ textAlign: "center" }}
                >
                  No available data
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={journalList?.filter?.totalDocs}
          rowsPerPage={journalList?.filter?.rowsPerPage}
          page={journalList?.filter?.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiToolbar-root": {
              minHeight: "48px",
            },
          }}
        />
      </TableContainer>
      <CustomDialog
        open={deleteJournal.warningOpen}
        title={"Are you sure ?"}
        agreeButtonText={"Delete"}
        agreeButtonProp={{
          type: "button",
          endIcon: deleteJournal.loading && <CircularProgress size={20} />,
          disabled: deleteJournal.loading,
          onClick: () => onClickDeleteSubject(),
          size: "small",
          sx: {
            "&.MuiButton-root": {
              color: theme.palette.neutral[0],
              // width: "100%",
              borderRadius: theme.borderRadius[10],
              backgroundColor: theme.palette.secondary.main,
              textTransform: "none",
              "&:hover": {
                backgroundColor: theme.palette.secondary.hover,
              },
            },
          },
        }}
        cancelButtonText={"Cancel"}
        cancelButtonProp={{
          type: "button",
          disabled: deleteJournal.loading,
          onClick: () =>
            setDeleteJournal((prev) => ({
              ...prev,
              id: null,
              warningOpen: false,
              loading: false,
            })),
          size: "small",
          sx: {
            "&.MuiButton-root": {
              color: theme.palette.neutral[0],
              // width: "100%",
              borderRadius: theme.borderRadius[10],
              backgroundColor: theme.palette.primary.main,
              textTransform: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.hover,
              },
            },
          },
        }}
      >
        <WarningDescText>
          Do you really want to delete this record? This process cannot be
          undone.
        </WarningDescText>
      </CustomDialog>
    </Box>
  );
};

export default JournalList;
